import React, { useEffect, useRef, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import Select, { components } from 'react-select';
import { toast } from 'react-toastify';

import cashierDutyServices from '@C/CashiersOnDuty/_utils/data';
import { ToggleSwitch } from '@C/FormElements';
import { broadcastConfigUpdate } from '@C/Utils';
import storeApis from '@stores/_utils/data';
import {
  ENABLE_TIME_LOGGING_SETTING_ID,
  formatTimeTo12Hour,
  getShiftTimingConfig,
  SHIFT_TIMINGS_SETTING_ID,
  TIME_OPTIONS,
} from '@stores/_utils/bunkConfig';
import { useGlobalStore } from '@stores/globalStore';

import settingsSchema from './settings.json';

const schemaSections = settingsSchema.sections || [];
const toggleFields = schemaSections
  .flatMap((section) => section.fields || [])
  .filter((field) => field.type === 'toggle');
const shiftTimingSection =
  schemaSections.find((section) => section.type === 'shift-timings') || {};
const shiftTimingFields = shiftTimingSection.fields || [];
const timeOptions = TIME_OPTIONS.map((option) => ({
  value: option,
  label: formatTimeTo12Hour(option),
}));

const TimeMenuList = (props) => {
  const menuListRef = useRef(null);

  useEffect(() => {
    const selectedOption = menuListRef.current?.querySelector(
      '[aria-selected="true"]',
    );

    if (selectedOption) {
      selectedOption.scrollIntoView({
        block: 'nearest',
      });
    }
  }, []);

  return (
    <components.MenuList {...props} innerRef={menuListRef}>
      {props.children}
    </components.MenuList>
  );
};

const buildFormState = (storedConfig = {}, shiftList = []) => {
  const enableTimeLoggingField = toggleFields.find(
    (field) => field.id === ENABLE_TIME_LOGGING_SETTING_ID,
  );
  const shiftTimings = shiftList.reduce((acc, shift) => {
    const timing = getShiftTimingConfig(storedConfig, shift.value, shiftList);
    acc[shift.value] = {
      start_time: timing.start_time,
      end_time: timing.end_time,
    };
    return acc;
  }, {});

  return {
    [ENABLE_TIME_LOGGING_SETTING_ID]: Boolean(
      storedConfig?.[ENABLE_TIME_LOGGING_SETTING_ID] ??
      enableTimeLoggingField?.defaultValue,
    ),
    [SHIFT_TIMINGS_SETTING_ID]: shiftTimings,
  };
};

const ShiftSettings = () => {
  const bunkConfig = useGlobalStore((state) => state.bunkConfig);
  const bunkConfigUuid = useGlobalStore((state) => state.bunkConfigUuid);
  const fetchBunkConfig = useGlobalStore((state) => state.fetchBunkConfig);
  const shiftList = useGlobalStore((state) => state.shiftList);
  const fetchShiftList = useGlobalStore((state) => state.fetchShiftList);
  const [config, setConfig] = useState(() => buildFormState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasActiveDuties, setHasActiveDuties] = useState(false);

  useEffect(() => {
    if (!shiftList.length) {
      fetchShiftList();
    }
  }, [fetchShiftList, shiftList.length]);

  useEffect(() => {
    cashierDutyServices
      .getActiveDutyList()
      .then((response) => {
        const activeDuties = response.data?.data || [];
        if (!activeDuties.length) {
          return;
        }

        setHasActiveDuties(true);
        confirmAlert({
          closeOnEscape: false,
          closeOnClickOutside: false,
          customUI: ({ onClose }) => (
            <div className="react-confirm-alert-body">
              <h1>Shift settings locked</h1>
              <p>
                There are cashiers currently on active duty. All active
                duties must be completed before shift settings can be
                modified.
              </p>
              <div className="react-confirm-alert-button-group">
                <button onClick={onClose}>OK</button>
              </div>
            </div>
          ),
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  useEffect(() => {
    setConfig(buildFormState(bunkConfig, shiftList));
  }, [bunkConfig, shiftList]);

  const timeLoggingEnabled = Boolean(config?.[ENABLE_TIME_LOGGING_SETTING_ID]);

  const shiftRows = shiftList.map((shift) => ({
    shiftId: shift.value,
    shiftLabel: shift.label,
    timing: config?.[SHIFT_TIMINGS_SETTING_ID]?.[shift.value] || {
      start_time: '00:00',
      end_time: '00:00',
    },
  }));

  const handleToggleChange = (id, value) => {
    setConfig((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleShiftTimeChange = (shiftId, fieldId, value) => {
    setConfig((prev) => ({
      ...prev,
      [SHIFT_TIMINGS_SETTING_ID]: {
        ...(prev?.[SHIFT_TIMINGS_SETTING_ID] || {}),
        [shiftId]: {
          ...(prev?.[SHIFT_TIMINGS_SETTING_ID]?.[shiftId] || {}),
          [fieldId]: value,
        },
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (hasActiveDuties) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      [ENABLE_TIME_LOGGING_SETTING_ID]: Boolean(
        config?.[ENABLE_TIME_LOGGING_SETTING_ID],
      ),
      [SHIFT_TIMINGS_SETTING_ID]: shiftRows.reduce((acc, shift) => {
        acc[shift.shiftId] = {
          start_time: shift.timing.start_time,
          end_time: shift.timing.end_time,
        };
        return acc;
      }, {}),
    };

    try {
      if (bunkConfigUuid) {
        await storeApis.putBunkConfig(bunkConfigUuid, payload);
      } else {
        await storeApis.postBunkConfig(payload);
      }
      await fetchBunkConfig();
      broadcastConfigUpdate(
        'Shift settings have been updated. This page will reload to apply the latest configuration.',
      );
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to save shift settings',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="widget full-width form-table">
      <main>
        <div>
          <p>Shift settings</p>
        </div>
      </main>
      <main>
        <div className="form-content">
          {hasActiveDuties && (
            <div className="notification-alert notification-alert__error">
              Shift settings are locked while cashiers are on active duty.
              Complete all active duties before making changes.
            </div>
          )}

          <table>
            <thead>
              <tr>
                <th>Setting</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {toggleFields.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="input-wrapper">
                      <span>{item.label}</span>
                    </div>
                  </td>
                  <td>
                    <div className="input-wrapper">
                      <ToggleSwitch
                        id={`toggle-${item.id}`}
                        name={item.id}
                        checked={Boolean(config?.[item.id])}
                        onChange={(e) =>
                          handleToggleChange(item.id, e.target.checked)
                        }
                        optionLabels={['On', 'Off']}
                        disabled={hasActiveDuties}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table style={{ marginTop: '1.5rem' }}>
            <thead>
              <tr>
                <th>Shift</th>
                {shiftTimingFields.map((field) => (
                  <th key={field.id}>{field.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shiftRows.length ? (
                shiftRows.map((shift) => (
                  <tr key={shift.shiftId}>
                    <td>
                      <div className="input-wrapper">
                        <span>{shift.shiftLabel}</span>
                      </div>
                    </td>
                    {shiftTimingFields.map((field) => (
                      <td key={field.id}>
                        <div className="input-wrapper">
                          <Select
                            options={timeOptions}
                            components={{ MenuList: TimeMenuList }}
                            value={timeOptions.find(
                              (option) =>
                                option.value ===
                                (shift.timing?.[field.id] ||
                                  field.defaultValue),
                            )}
                            onChange={(selectedOption) =>
                              handleShiftTimeChange(
                                shift.shiftId,
                                field.id,
                                selectedOption?.value || field.defaultValue,
                              )
                            }
                            isDisabled={!timeLoggingEnabled || hasActiveDuties}
                            className="select-box-container"
                            classNamePrefix="shift-selector"
                            menuPortalTarget={document.body}
                            isSearchable={true}
                          />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={shiftTimingFields.length + 1}>
                    <div className="input-wrapper">
                      <span>No shifts available</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="form-actions">
            <button
              className="submit"
              disabled={isSubmitting || hasActiveDuties}
              onClick={handleSubmit}
            >
              Apply
            </button>
          </div>
        </div>
      </main>
    </section>
  );
};

export { ShiftSettings, ShiftSettings as BunkConfig };
